
import { useEffect, useState } from "react";
import { Box, Tooltip, } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridActionsCellItem, GridColDef, GridColumnVisibilityModel, GridRowParams } from "@mui/x-data-grid-pro";
import { DateFilterOperator, NumberFilterOperator, TextFilterOperator } from "components/shared/filters";

import moment from "moment";
import { formatDate } from "utils/date-utils";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IDashboardSettings } from "components/preferences";
import { SetPreferenceSignature } from "hooks/preferences";
import { Ico_Delete, Ico_Edit, Ico_Eye, Ico_Secure } from "@sagecentral/core-ui";
import { FileOrLink, IFileAttachment } from "models/attachments";
import IsAttachementFileOrUrlFilterOperator from "components/shared/filters/IsAttachementFileOrUrlFilterOperator";
import IWorkItemPermissions from "models/work-items/IWorkItemPermissions";
import { isBrowserViewable } from "utils/attachment-utils";

type UseWorkItemAttachmentsColumnsProps = {
  settings?: IDashboardSettings | null,
  draft?: boolean,
  workItemPermissions: IWorkItemPermissions | null,
  setSettings?: SetPreferenceSignature,
  openDeleteAttachment: (attachmentId: number | string, attachmentName: string) => void,
  openAttachment: (attachmentId: number, attachmentType: string, attachmentName: string) => void,
  handleEditAttachment: (attachment: any) => void,
  viewAttachment: (attachmentId: number | string, attachmentSize: string, attachmentName: string) => void,
};

type UseWorkItemAttachmentsColumnsResult = [
  GridColDef[],
  GridColumnVisibilityModel,
  ColumnVisibilityOrOrderChangeHandler,
];

type UseWorkItemAttachmentsColumnsSignature = (props: UseWorkItemAttachmentsColumnsProps) => UseWorkItemAttachmentsColumnsResult;

const UseWorkItemAttachmentsColumns: UseWorkItemAttachmentsColumnsSignature = (props) => {
  const { settings, setSettings } = props;
  const columnBase: GridColDef[] = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      hideable: false
    },
    {
      field: 'attachmentType', headerName: 'Type', width: 150, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
    },
    {
      field: 'attachmentName', headerName: 'Attachment Name', flex: 1, minWidth: 200, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, disableReorder: true,
      filterOperators: TextFilterOperator,
      renderCell: (params) => {
        const name = params.value;
        const id = params.row.workItemInstanceAttachmentID;
        const attachmentTypeId = params.row.isAttachementFileOrURL;
        const attachmentSize = params.row.size;
        const attachmentName = params.row.attachmentName;
        return (
          <Tooltip title={name} arrow>
            <Box>
              {
                params.row.attachmentIdOpening ? <span>{name}</span> :
                  (params.row.isAttachementFileOrURL === FileOrLink[FileOrLink.Link] &&
                    <a
                      href={params.row.filePath}
                      target="_blank"
                      style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }}
                      rel="noopener noreferrer"
                      aria-label={`Open link: ${name}`}
                      // Make first row's link the initial focus target
                      tabIndex={params.id === params.api.getRowModels().values().next().value?.id ? 0 : 0}
                      data-first-actionable={params.id === params.api.getRowModels().values().next().value?.id ? 'true' : undefined}
                    >
                      {name}
                    </a> ||
                    <a
                      onClick={() => props.openAttachment(props.draft ? params.row.id! : id, attachmentSize, attachmentName)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          props.openAttachment(props.draft ? params.row.id! : id, attachmentSize, attachmentName);
                        }
                      }}
                      style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }}
                      rel="noopener noreferrer"
                      role="button"
                      // Make first row's link the initial focus target
                      tabIndex={params.id === params.api.getRowModels().values().next().value?.id ? 0 : 0}
                      data-first-actionable={params.id === params.api.getRowModels().values().next().value?.id ? 'true' : undefined}
                      aria-label={`Open attachment: ${name}`}
                    >
                      {name}
                    </a>)
              }
            </Box>
          </Tooltip>
        );
      }
    },
    {
      field: 'version', headerName: 'Version', width: 150, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false,
      filterOperators: NumberFilterOperator,
      renderHeaderFilter: () => (null),
      renderCell: (params) => {
        const version = params.value;
        return (
          <div>
            {version}
          </div>
        );
      }
    },
    {
      field: 'isAttachementFileOrURL', headerName: 'L/F/S', width: 150, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false,
      filterOperators: IsAttachementFileOrUrlFilterOperator,
      renderHeader: (params) => {
        return (
          <Tooltip title="Link, File, SFTP" arrow>
            <div aria-label="Link, File, or SFTP attachment type">{"L/F/S"}</div>
          </Tooltip>
        )
      },
      renderCell: (params) => {
        const isSensitiveAttachment = params.row.isSensitiveAttachment;
        const attachmentType = params.value;
        return (
          <>
            {attachmentType}
            {isSensitiveAttachment && <svg fill="none" height={24} width={24}><Ico_Secure /></svg>}
          </>
        )
      }
    },
    {
      field: 'createdByName', headerName: 'Added by', width: 150, sortable: true, resizable: true, hideable: true,
      headerClassName: 'header', hideSortIcons: false, filterOperators: TextFilterOperator
    },
    {
      field: 'fileCreatedDate', headerName: 'Date Added', width: 150, sortable: true, resizable: true, hideable: true, headerClassName: 'header', hideSortIcons: false, type: 'date',
      valueGetter: (value) => {
        return !value ? null : moment.utc(value).toDate();
      },
      filterOperators: DateFilterOperator,
      renderCell: (params) => {
        const milestoneDate = params.value;
        return (
          <div>
            {formatDate(milestoneDate)}
          </div>
        );
      }
    },
    {
      field: 'size', headerName: 'File Size', width: 150, sortable: true, resizable: true, hideable: false, headerClassName: 'header', hideSortIcons: false, filterOperators: NumberFilterOperator,
      renderHeaderFilter: () => (null),
      renderCell: (params) => {
        const kbSize = params.value;
        return (
          <div>
            {kbSize && kbSize !== '' ? Math.round(kbSize.replaceAll(",", "")).toLocaleString('en-US') + ' KB' : ''}
          </div>
        );
      }
    },
    {
      field: 'Tools',
      headerName: 'Tools',
      type: 'actions',
      width: 70,
      disableReorder: true,
      hideable: false,
      getActions: (params: GridRowParams<IFileAttachment>) => {
        const actions = [];
        const attachmentId = params.row.id;
        const attachmentSize = params.row.size;
        const attachmentName = params.row.attachmentName;
        const isLarge = attachmentSize && parseInt(attachmentSize.replaceAll(',', '')) > 100000;

        if (props.workItemPermissions?.canDeleteAttachments || params.row.createdBy == params.row.currentUserId) {
          actions.unshift(
            <GridActionsCellItem
              icon={<Ico_Delete sx={{ color: "#001A69" }} />}
              label="Delete"
              onClick={() => props.openDeleteAttachment(props.draft ? params.row.id! : params.row.workItemInstanceAttachmentID, params.row.attachmentName)}
              showInMenu
              aria-label={`Delete attachment: ${params.row.attachmentName}`}
            />
          )
        }

        if (params.row.isAttachementFileOrURL !== 'File') {
          if (params.row.isAttachementFileOrURL === 'Link' || params.row.uploadStatus !== 'SUCCESS') {
            actions.unshift(
              <GridActionsCellItem
                icon={<Ico_Edit sx={{ color: "#001A69" }} />}
                label="Edit"
                onClick={() => props.handleEditAttachment(params.row)}
                showInMenu
                aria-label={`Edit attachment: ${params.row.attachmentName}`}
              />
            );
          }
        }

        if (params.row.isAttachementFileOrURL === FileOrLink[FileOrLink.File] && !isLarge) {
          actions.unshift(
            <GridActionsCellItem
              icon={Ico_Eye}
              label="View"
              onClick={() => props.viewAttachment(attachmentId, attachmentSize, attachmentName)}
              showInMenu
              aria-label={`View attachment: ${params.row.attachmentName}`}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  props.viewAttachment(attachmentId, attachmentSize, attachmentName);
                }
              }}
            />
          );
        }
        return actions;
      }
    }
  ];

  const [columns, setColumns] = useState<GridColDef[]>(columnBase);

  const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns, settings, setSettings })

  useEffect(() => {
    let newColumns: GridColDef[] = [];
    newColumns.push(...columnBase);

    if (props.draft) {
      const versionIdx = newColumns.findIndex((c) => c.field === 'version');
      if (versionIdx > -1) {
        newColumns.splice(versionIdx, 1);
      }
    }


    setColumns(newColumns);
  }, []);

  return [
    columnsModel,
    visibilityModel,
    handleColumnVisibilityOrOrderChange
  ];
};

export default UseWorkItemAttachmentsColumns;
